import { EVENTS } from '../../../shared/EventBus';
import type { AbilityResourceResultEvent } from '../../../shared/types/NetworkMessages';
import { formatResourceAmount } from '../../../shared/combat/bloodwellExchange';
import { COMBAT_TEXT_BEATS } from '../../../config/abilities/combatTextBeats.config';
import { abilityPalette } from '../abilityPalette';
import type { FloatingTextManager, StyledFloatingTextConfig } from '../managers/FloatingTextManager';

type AcceptedExchange = Extract<AbilityResourceResultEvent, { outcome: 'accepted' }>;
export const BLOODWELL_EXCHANGE_STYLE = {
  // Clear the player's name/HP/MP stack and nearby service captions.
  fontSize: '14px', duration: 1200, offsetY: 130, style: 'info', verb: 'drift', tier: 0, alwaysRender: false,
} as const satisfies Pick<StyledFloatingTextConfig, 'fontSize' | 'duration' | 'offsetY' | 'style' | 'verb' | 'tier' | 'alwaysRender'>;
const RECEIPT_LIMIT = 256;

/** The core ingress owns room/actor/life validation and emits accepted results
 * only. This view never mutates resources or synthesizes damage/healing events. */
export class BloodwellExchangePresentation {
  private readonly seen = new Set<string>();
  constructor(private readonly text: Pick<FloatingTextManager, 'showFloatingText'>) {}

  register(sub: (event: string, handler: (data: any) => void) => void): void {
    sub('combat:resource_exchange', (data: AcceptedExchange) => this.show(data));
    sub(EVENTS.NET_DISCONNECTED, () => this.seen.clear());
    sub(EVENTS.MAP_SERVER_SWITCH_START, () => this.seen.clear());
  }

  private show(data: AcceptedExchange): void {
    if (!data || data.outcome !== 'accepted' || data.abilityId !== 'life_tap'
      || typeof data.resourceExchangeId !== 'string' || !data.resourceExchangeId
      || ![data.x, data.y, data.healthSpent, data.manaRestored].every(Number.isFinite)
      || data.healthSpent < 0 || data.manaRestored < 0 || this.seen.has(data.resourceExchangeId)) return;
    if (this.seen.size >= RECEIPT_LIMIT) this.seen.delete(this.seen.values().next().value!);
    this.seen.add(data.resourceExchangeId);
    const label = `−${formatResourceAmount(data.healthSpent)} HP · +${formatResourceAmount(data.manaRestored)} MP`;
    const pale = abilityPalette('life_tap')?.palette[2];
    const color = pale === undefined ? COMBAT_TEXT_BEATS.manaRestore.color : `#${pale.toString(16).padStart(6, '0')}`;
    // FloatingTextManager subtracts offsetY once; x/y are the frozen server body centre.
    this.text.showFloatingText(label, data.x, data.y, {
      ...BLOODWELL_EXCHANGE_STYLE, text: label, color, targetKey: data.casterSid,
    });
  }
}
